import React, { useMemo, useState, useEffect, useContext } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";

import MenuIcon from "@mui/icons-material/Menu";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
// import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { Context as AuthContext } from "../context/Auth";

const DRAWER_WIDTH = 280;

type NavItem = {
    label: string;
    to: string;
    icon: React.ReactNode;
};

const isSelected = (pathname: string, to: string) =>
    to === "/dashboard" ? pathname === to : pathname.startsWith(to);

export default function DashboardLayout() {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const [mobileOpen, setMobileOpen] = useState(false);
    const { pathname } = useLocation();
    const { state: authState } = useContext(AuthContext);

    const topNavItems: NavItem[] = useMemo(
        () => [
            {
                label: "Create",
                to: "/dashboard/create",
                icon: <PlayArrowOutlinedIcon fontSize="small" />,
            },
            {
                label: "Library",
                to: "/dashboard/library",
                icon: <PlayArrowOutlinedIcon fontSize="small" />,
            },
            {
                label: "Pricing",
                to: "/dashboard/pricing",
                icon: <CreditCardOutlinedIcon fontSize="small" />,
            },
            {
                label: "Support",
                to: "/dashboard/support",
                icon: <HelpOutlineIcon fontSize="small" />,
            },
        ],
        [],
    );

    // Mobile drawer content
    const mobileDrawer = (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Toolbar sx={{ px: 2, minHeight: 64 }}>
                <Box
                    component="img"
                    src="/icons8-sound-64.png"
                    alt="Text Voice"
                    sx={{
                        height: 28,
                        width: "auto",
                        objectFit: "contain",
                    }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, ml: 1 }}>
                    Text Voice
                </Typography>
            </Toolbar>
            <Divider />
            <List sx={{ px: 1, py: 1 }}>
                {topNavItems.map((item) => {
                    const selected = isSelected(pathname, item.to);
                    return (
                        <ListItemButton
                            key={item.to}
                            component={NavLink}
                            to={item.to}
                            onClick={() => setMobileOpen(false)}
                            selected={selected}
                            sx={{
                                borderRadius: 2,
                                mx: 0.5,
                                my: 0.25,
                                py: 1,
                            }}
                        >
                            {/* <ListItemIcon sx={{ minWidth: 36 }}>
                                {item.icon}
                            </ListItemIcon> */}
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    );
                })}
            </List>
        </Box>
    );

    // Get user initial for avatar (fallback if no profile picture)
    const userInitial = authState.user?.id
        ? authState.user.id.charAt(0).toUpperCase()
        : "U";

    // Use Google profile picture if available, otherwise use initial
    const avatarSrc = authState.user?.profilePicture;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
            }}
        >
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: "background.paper",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Box
                    sx={{
                        maxWidth: { xs: "100%", sm: 1200, md: 1400 },
                        width: "100%",
                        mx: "auto",
                        px: { xs: 2, sm: 3, md: 4 },
                    }}
                >
                    <Toolbar
                        disableGutters
                        sx={{
                            minHeight: 64,
                            gap: 3,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Box
                                component="img"
                                src="/icons8-sound-64.png"
                                alt="Text Voice"
                                sx={{
                                    height: 28,
                                    width: "auto",
                                    objectFit: "contain",
                                }}
                            />
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 700,
                                    display: { xs: "none", sm: "block" },
                                }}
                            >
                                Text Voice
                            </Typography>
                        </Box>
                        {/* Mobile menu button */}
                        {!isDesktop && (
                            <IconButton
                                color="inherit"
                                edge="start"
                                onClick={() => setMobileOpen((v) => !v)}
                                sx={{ mr: 1 }}
                                aria-label="open navigation"
                            >
                                <MenuIcon />
                            </IconButton>
                        )}

                        {/* Navigation items - desktop only */}
                        {isDesktop && (
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    flexGrow: 1,
                                    ml: 2,
                                }}
                            >
                                {topNavItems.map((item) => {
                                    const selected = isSelected(
                                        pathname,
                                        item.to,
                                    );
                                    return (
                                        <Button
                                            key={item.to}
                                            component={NavLink}
                                            to={item.to}
                                            variant="text"
                                            // startIcon={item.icon}
                                            sx={{
                                                textTransform: "none",
                                                borderRadius: 0,
                                                px: 2,
                                                py: 1,
                                                fontWeight: 600,
                                                position: "relative",
                                                color: "text.primary",
                                                fontSize: "0.875rem !important",
                                                "&:hover": {
                                                    backgroundColor:
                                                        theme.palette.action
                                                            .hover,
                                                },
                                                ...(selected && {
                                                    color: theme.palette.primary
                                                        .main,
                                                    "&::after": {
                                                        content: '""',
                                                        position: "absolute",
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: 2,
                                                        backgroundColor:
                                                            theme.palette
                                                                .primary.main,
                                                    },
                                                }),
                                            }}
                                        >
                                            {item.label}
                                        </Button>
                                    );
                                })}
                            </Box>
                        )}

                        {/* Spacer */}
                        <Box sx={{ flexGrow: 1 }} />

                        {/* User avatar or login buttons */}
                        {authState.user ? (
                            <Link
                                to="/dashboard/settings"
                                style={{
                                    textDecoration: "none",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <Avatar
                                    src={avatarSrc}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        bgcolor: avatarSrc
                                            ? undefined
                                            : theme.palette.primary.main,
                                        fontSize: "0.9rem",
                                        fontWeight: 600,
                                        cursor: "pointer",
                                    }}
                                >
                                    {!avatarSrc && userInitial}
                                </Avatar>
                            </Link>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: { xs: 0.75, sm: 1.5 },
                                }}
                            >
                                <Button
                                    component={Link}
                                    to="/login"
                                    variant="outlined"
                                    size={isDesktop ? "medium" : "small"}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: {
                                            xs: "0.75rem",
                                            sm: "0.875rem",
                                        },
                                        px: { xs: 1, sm: 2 },
                                        py: { xs: 0.5, sm: 1 },
                                    }}
                                >
                                    Log in
                                </Button>
                                <Button
                                    component={Link}
                                    to="/login"
                                    variant="contained"
                                    size={isDesktop ? "medium" : "small"}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: {
                                            xs: "0.75rem",
                                            sm: "0.875rem",
                                        },
                                        px: { xs: 1, sm: 2 },
                                        py: { xs: 0.5, sm: 1 },
                                        backgroundColor:
                                            theme.palette.primary.main,
                                        "&:hover": {
                                            backgroundColor:
                                                theme.palette.primary.dark,
                                        },
                                    }}
                                >
                                    Try for free
                                </Button>
                            </Box>
                        )}
                    </Toolbar>
                </Box>
            </AppBar>

            {/* Mobile drawer */}
            {!isDesktop && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: "block", md: "none" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: DRAWER_WIDTH,
                        },
                    }}
                >
                    {mobileDrawer}
                </Drawer>
            )}

            {/* Main content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: "100%",
                    pt: { xs: 2, sm: 3 },
                    px: { xs: 1.5, sm: 2 },
                    pb: 3,
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
}
